import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../../services/auth.api';
import { useAuth } from './AuthContext';
import { Button } from '@/components/ui';

const ResendVerification: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { user } = useAuth();

  const handleResend = async () => {
    setIsSubmitting(true);

    try {
      await authAPI.resendVerification();
      setEmailSent(true);
    } catch (error) {
      console.error('Resend verification error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-meradio-primary/10 to-meradio-secondary/10 dark:from-meradio-primary/5 dark:to-meradio-secondary/5 flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-meradio-primary/10 dark:bg-meradio-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-meradio-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-meradio-dark dark:text-meradio-cream mb-2">
              Email Sent!
            </h1>
            <p className="text-meradio-dark-light dark:text-meradio-dark-light">
              We've sent a verification email to{' '}
              <span className="font-medium text-meradio-dark dark:text-meradio-cream">
                {user?.email}
              </span>
              . Please check your inbox and click the verification link.
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-meradio-dark-light dark:text-meradio-dark-light">
              Didn't receive the email? Check your spam folder or wait a few minutes before requesting another.
            </p>

            <Button variant="link" size="sm" onClick={() => setEmailSent(false)}>
              Try again
            </Button>
          </div>

          <div className="mt-6 pt-6 border-t border-meradio-dark/10 dark:border-meradio-cream/10">
            <Link
              to="/dashboard"
              className="text-sm text-meradio-secondary hover:text-meradio-secondary-dark dark:hover:text-meradio-secondary transition-colors font-medium"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-meradio-primary/10 to-meradio-secondary/10 dark:from-meradio-primary/5 dark:to-meradio-secondary/5 flex items-center justify-center p-4">
      <div className="card max-w-md w-full text-center">
        <div className="mb-6">
          <h1 className="text-3xl font-bold gradient-text mb-2">Resend Verification Email</h1>
          <p className="text-meradio-dark-light dark:text-meradio-dark-light">
            Click the button below to receive a new email verification link.
          </p>
        </div>

        {user?.email && (
          <p className="text-sm text-meradio-dark-light dark:text-meradio-dark-light mb-6">
            We'll send the verification email to:{' '}
            <span className="font-medium text-meradio-dark dark:text-meradio-cream block mt-1">
              {user.email}
            </span>
          </p>
        )}

        <Button onClick={handleResend} fullWidth isLoading={isSubmitting} disabled={isSubmitting}>
          {isSubmitting ? 'Sending...' : 'Send Verification Email'}
        </Button>

        <div className="mt-6 text-center">
          <Link
            to="/dashboard"
            className="text-sm text-meradio-secondary hover:text-meradio-secondary-dark dark:hover:text-meradio-secondary transition-colors font-medium"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResendVerification;
